import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from "react-icons/md";
import { MdDeleteForever } from "react-icons/md"; // Icon for Delete
import { Layout, CategoryProfile, CategoryConfig, DeleteConfirmationModal } from '../../../../components'; // <-- UPDATED IMPORT
import { handleApi } from '../../../../utils/utils';
import AppContext from '../../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const DeleteCategory = () => {
  const location = useLocation();
  // Data of the category to be deleted, passed from the list page
  const initialCategory = location.state?.initialCategory;
  
  // Initialize react-hook-form (even though fields are read-only, we need control for consistency)
  const { reset, control, register, formState: { errors } } = useForm({
    defaultValues: initialCategory
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [displayImg, setDisplayImg] = useState(''); 
  const [imgType, setImgType] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false); // State for modal control
  const { userData, currentColor, actionCount, setActionCount } = useContext(AppContext);

  const navigate = useNavigate(); 
  
  // --- Image Handling Logic ---

  // Fetches image from URL and converts it to base64 for display
  const fetchImageAsBase64 = async (url) => {
    setIsImageLoading(true);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const reader = new FileReader();
      reader.readAsDataURL(blob);
      reader.onloadend = () => {
        const base64data = reader.result.split(',')[1];
        const contentType = blob.type;
        setDisplayImg(base64data);
        setImgType(contentType);
      };
    } catch (error) {
      console.error("Failed to fetch image from URL", error);
      setDisplayImg('');
      setImgType('');
    } finally {
      setIsImageLoading(false);
    }
  };
  
  // Effect to load initial category photo
  useEffect(() => {
    if (initialCategory?.photo_url) {
      fetchImageAsBase64(initialCategory.photo_url);
    } else if (initialCategory?.photo?.data && initialCategory?.photo?.contentType) {
      setDisplayImg(initialCategory.photo.data);
      setImgType(initialCategory.photo.contentType);
    } else {
      setDisplayImg('');
      setImgType('');
    }
  }, [initialCategory]);


  // --- Action Handlers ---
  
  // Function to show the confirmation modal
  const openDeleteModal = () => {
    if (!initialCategory?.id) {toast.error("Cannot delete, category ID is missing.");return;}
    setIsModalOpen(true);
  };

  const handleCancelModal = () => {setIsModalOpen(false);};  // Function to cancel the delete process and close the modal

  // Function that executes the delete API call upon modal confirmation
  const confirmDeleteCategory = async () => {
      setIsModalOpen(false); // Close modal before API call
      
      // Safety check (redundant, but good practice)
      if (!initialCategory || !initialCategory.id) { toast.error("Error: Cannot delete category. ID is missing.");return;}

      setIsLoading(true);
      const apiEndpoint = 'product_categories'; 
      
      try {
          const response = await handleApi(apiEndpoint, {}, userData, 'DELETE',  { id: initialCategory.id });
          
          if (response.status === 200) {
            toast.success(response.data.message || `Category '${initialCategory.name}' deleted successfully!`);
            setActionCount(actionCount + 1); // Trigger refresh in listing pages
            navigate(-1); // Go back to the list page
          } else {
            // Handle server-side errors or non-200 responses
            toast.error(response.data?.message || 'Error deleting category!');
          }
      } catch (error) {
          toast.error(error.message || 'An unexpected error occurred during deletion!');
      } finally {
          setIsLoading(false);
      }
  };

  const handleCancel = () => {
    // Simply navigate back 
    navigate(-1);
  };

  
  // --- UI/Rendering Logic ---

  // Default SVG for placeholder when no photo is available
  const svgString = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="${currentColor}" width="100" height="100">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6m6 0a2.25 2.25 0 012.25-2.25h2.25A2.25 2.25 0 0121 6m-18 0v1.5c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125V6M3 6h18m-18 8.25V18a2.25 2.25 0 002.25 2.25h13.5A2.25 2.25 0 0021 18v-3.75m-18 0V12h18v2.25" />
  </svg>`;
  const base64Svg = btoa(unescape(encodeURIComponent(svgString)));
  const defaultPhotoUrl = `data:image/svg+xml;base64,${base64Svg}`;

  // Determine the photo URL to display
  const photoUrl = isImageLoading ? null : (displayImg && imgType ? `data:${imgType};base64,${displayImg}` : defaultPhotoUrl);

  return (
    <Layout>
       <div className="min-h-screen text-white p-2 sm:p-4">
        <div className="relative flex justify-between items-center">
          <h1 className="text-center font-bold flex-grow text-gray-800 text-xl">DELETE CATEGORY</h1>
          
          {/* Action Button Group */}
          <div className="flex gap-1 sm:gap-2 mr-2"> 
            
            {/* Delete Confirmation Button - now opens the modal */}
            <button
              onClick={openDeleteModal}
              disabled={isLoading || !initialCategory?.id}
              className="p-2 rounded-full text-red-400 bg-gray-700/50 hover:bg-red-500 hover:text-white transition-all duration-300 shadow-md border border-red-500/50 disabled:opacity-50"
              aria-label="Confirm Delete"
              title="Delete Category"
            >
              {isLoading ? (
                <div className="animate-spin h-5 w-5 border-2 border-red-200 border-t-red-600 rounded-full"></div>
              ) : (
                <MdDeleteForever className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </button>

            {/* Close/Back Button */}
            <button
              onClick={handleCancel}
              className="p-2 rounded-full text-gray-800 bg-white hover:bg-gray-300 transition-all duration-300 shadow-md"
              aria-label="Go back"
            >
              <MdOutlineCancel className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <ToastContainer position="top-center" autoClose={2000} />

        {isLoading && initialCategory?.id ? ( // Show loading only for API submit
          <div className="flex items-center flex-col py-6">
            <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
            <p className="mt-2 text-white">Deleting. Please wait...</p>
          </div>
        ) : (
          <div className="mx-auto border-b border-gray-400 bg-gray-500 transition-all duration-200 cursor-pointer rounded-xl shadow-2xl p-6 w-fit">
              <CategoryProfile
                  fields={CategoryConfig}
                  control={control}
                  register={register}
                  photo={photoUrl}
                  onSubmit={openDeleteModal} // Ensure form submission also opens the modal
                  categoryData={initialCategory}
                  onCancel={handleCancel}
                  errors={errors}
                  reset={reset}
                  isLoading={isLoading}
                  isReadOnly={true} 
                />
              </div>
        )}
      </div>

      {/* Delete Confirmation Modal using reusable component */}
      {isModalOpen && (
        <DeleteConfirmationModal
          title="Delete Category"
          message="Are you sure you want to permanently delete this category?"
          actionLabel="Delete Permanently"
          onConfirm={confirmDeleteCategory} // Executes the API call on confirmation
          onCancel={handleCancelModal} // Closes the modal on cancel
          itemName={initialCategory?.name} // Pass the item name for context
          icon={<MdDeleteForever className="h-8 w-8 text-red-600" />} // Use the delete icon
        />
      )}
    </Layout>
  );
};

export default DeleteCategory;
